/**
 * Results Processor - Transforms raw engine outputs into cosmic-compatible format
 * 
 * Purpose: Bridge between assessment engines and cosmic UI components
 * - Handles all tier engine output formats (FREE, ENHANCED, PREMIUM, LEGACY)
 * - Generates missing constellation data using official patterns
 * - Standardizes data structures for cosmic components
 * - Preserves all original engine data
 */

import constellations from '../data/constellations';
import domains from '../data/domains';
import preferences from '../data/preferences';

export class ResultsProcessor {
  
  /**
   * Main transformation method - converts any engine output to cosmic format
   * @param {Object} rawResults - Raw engine output data
   * @returns {Object} Cosmic-compatible results object
   */
  static transform(rawResults) {
    if (!rawResults) {
      console.warn('ResultsProcessor: No raw results provided');
      return null;
    }
    
    console.log('🔄 ResultsProcessor: Starting transformation', rawResults);
    
    try {
      // 1. Detect engine type
      const engineType = this.detectEngineType(rawResults);
      console.log('🎯 Detected engine type:', engineType);
      
      // 2. Extract domain scores from engine-specific format
      const domains = this.extractDomainScores(rawResults, engineType);
      console.log('📊 Extracted domain scores:', domains);
      
      // 3. Create standardized topPreferences array
      const topPreferences = this.formatTopPreferences(rawResults, engineType);
      console.log('🎯 Formatted top preferences:', topPreferences);
      
      // 4. Calculate top domains for constellation
      const topDomains = this.getTopDomains(domains, 2);
      console.log('🌟 Top domains for constellation:', topDomains);
      
      // 5. Generate constellation data from official patterns
      const constellationData = this.generateConstellationData(topDomains);
      console.log('✨ Generated constellation data:', constellationData);
      
      // 6. Add metadata and helper fields
      const metadata = this.generateMetadata(rawResults, engineType);
      
      // 7. Create final standardized structure
      const processedResults = {
        // Preserve all original engine data
        ...rawResults,

        // Normalize the full element list so every entry carries BOTH `domain`
        // and `domainId` (+ a resolved elementId). Engines key elements by
        // `domainId`, but several cosmic consumers (e.g. the Domains tab) filter
        // on `domain`; without this, Premium's all-45 grid showed every element
        // as "?" because the domain filter never matched.
        ...(Array.isArray(rawResults.allElements)
          ? { allElements: this.normalizeAllElements(rawResults.allElements) }
          : {}),

        // Add/override with standardized cosmic data
        constellationData,
        domains,
        topPreferences,
        topDomains,
        
        // Add helpful metadata
        ...metadata,
        
        // Processing info
        processedAt: new Date().toISOString(),
        processorVersion: 'cosmic-v1.0',
        originalEngineType: engineType
      };
      
      console.log('✅ ResultsProcessor: Transformation complete', processedResults);
      return processedResults;
      
    } catch (error) {
      console.error('❌ ResultsProcessor: Transformation failed', error);
      // Return original data with error flag rather than crashing
      return {
        ...rawResults,
        processingError: error.message,
        processedAt: new Date().toISOString(),
        processorVersion: 'cosmic-v1.0-error'
      };
    }
  }
  
  /**
   * Detect which engine generated the results by examining data structure
   * @param {Object} rawResults - Raw engine output
   * @returns {string} Engine type (FREE, ENHANCED, PREMIUM, LEGACY)
   */
  static detectEngineType(rawResults) {
    // Handle both uppercase and lowercase tier names
    const tier = rawResults.tier?.toUpperCase();
    
    if (tier === 'FREE' || tier === 'FREETIER') {
      return 'FREE';
    }
    
    if (tier === 'ENHANCED' || tier === 'ENHANCEDTIER') {
      return 'ENHANCED';
    }
    
    if (tier === 'PREMIUM' || tier === 'PREMIUMTIER') {
      return 'PREMIUM';
    }
    
    // Detect by data structure if tier field is missing
    if (rawResults.allElements && rawResults.domainSummary) {
      return 'PREMIUM';
    }
    
    if (rawResults.topElements && rawResults.overallReliability) {
      return 'ENHANCED';
    }
    
    if (rawResults.domains && rawResults.topPreferences) {
      return 'FREE';
    }
    
    // Legacy format detection
    if (rawResults.preferenceScores && rawResults.constellationData?.id) {
      return 'LEGACY';
    }
    
    console.warn('ResultsProcessor: Could not detect engine type, defaulting to LEGACY');
    return 'LEGACY';
  }
  
  /**
   * Extract domain scores from different engine formats
   * @param {Object} rawResults - Raw engine output
   * @param {string} engineType - Detected engine type
   * @returns {Object} Domain scores (0-100 format)
   */
  static extractDomainScores(rawResults, engineType) {
    // PRIORITY: If domains object already exists with percentile scores, use it directly
    // This handles cases where data already has pre-calculated domain percentages
    if (rawResults.domains && typeof rawResults.domains === 'object') {
      const domainValues = Object.values(rawResults.domains);
      // Check if these are already percentage scores (0-100 range)
      const hasValidScores = domainValues.length > 0 && 
        domainValues.every(v => typeof v === 'number' && v >= 0 && v <= 100);
      
      if (hasValidScores) {
        console.log('📊 Using existing domain scores (pre-calculated percentages)');
        return rawResults.domains;
      }
    }
    
    // Otherwise, extract based on engine type
    switch (engineType) {
      case 'FREE':
        // FreeTier already provides normalized domain scores
        return rawResults.domains || {};
        
      case 'ENHANCED':
        // Enhanced has ALL 45 element scores — use them for accurate domain calculation
        // Falls back to topElements if allElementScores is missing
        if (rawResults.allElementScores && typeof rawResults.allElementScores === 'object') {
          const allElements = Object.entries(rawResults.allElementScores).map(([id, s]) =>
            this.toScoredElement(id, s)
          );
          return this.calculateDomainsFromElements(allElements);
        }
        return this.calculateDomainsFromElements(rawResults.topElements || []);
        
      case 'PREMIUM': {
        // Premium tier can arrive in two shapes:
        //  (a) full assessment result with a domainSummary (averageTheta per domain), or
        //  (b) an instant-unlock result from PremiumTierEngine.unlockPremiumResults,
        //      which carries allElements/allElementScores but NO domainSummary and NO
        //      pre-calc domains. (b) was leaving the results page with empty 0% domains
        //      and a fallback constellation, so derive domains from the element scores
        //      (exactly like ENHANCED) whenever a usable summary is absent.
        // ELEMENTS BEAT THE SUMMARY WHEN BOTH ARE THERE (2026-09-14).
        // `extractDomainsFromSummary` converts a mean theta to a percentile,
        // which is percentile-of-mean rather than mean-of-percentiles, and it
        // cannot exclude an unmeasured row because the mean has already eaten
        // it. The element path can do both correctly, so it goes first and the
        // summary stays only as the answer for a document that has no elements.
        if (rawResults.allElementScores && typeof rawResults.allElementScores === 'object') {
          const els = Object.entries(rawResults.allElementScores)
            .map(([id, s]) => this.toScoredElement(s.elementId || id, s));
          return this.calculateDomainsFromElements(els);
        }
        if (Array.isArray(rawResults.allElements) && rawResults.allElements.length > 0) {
          const els = rawResults.allElements
            .map(s => this.toScoredElement(s.elementId || s.id || s.element_id, s))
            .filter(e => e.elementId);
          return this.calculateDomainsFromElements(els);
        }
        return this.extractDomainsFromSummary(rawResults.domainSummary || {});
      }
        
      case 'LEGACY':
        // Legacy format may have domainScores
        return rawResults.domainScores || rawResults.domains || {};
        
      default:
        console.warn('Unknown engine type, using empty domain scores');
        return {};
    }
  }
  
  /**
   * Calculate domain scores from element data (for Enhanced/Premium tiers)
   * @param {Array} elements - Array of element objects
   * @returns {Object} Domain scores
   */
  /**
   * A domain's score is the mean percentile of the elements the engine ACTUALLY
   * MEASURED in it.
   *
   * -------------------------------------------------------------------------
   * TWO RULES, AND EACH ONE WAS A REAL DEFECT (corrected 2026-09-14)
   * -------------------------------------------------------------------------
   * 1. AN UNMEASURED ELEMENT IS EXCLUDED, NOT AVERAGED IN.
   *    A row the engine never reached carries `reliability: 0` and a garbage
   *    `theta` (observed values across real runs include +4.00, +3.00, -4.00,
   *    -3.51). `convertThetaToPercentile` clamps at z 3.5, so `theta: 4` scored
   *    the 100th percentile and every element nobody measured contributed a
   *    perfect score to its domain. Absence of evidence became the strongest
   *    positive evidence in the profile. `isMeasured` is the whole fix, and it
   *    treats a MISSING `reliability` as measured on purpose: absence of the
   *    field is not absence of measurement.
   *
   * 2. THE PERCENTILE IS READ, NOT RE-DERIVED.
   *    The engine publishes `strengthPercentile` per element, which is a
   *    monotone transform of `theta * reliability` with a further per-element
   *    compression. Re-deriving 100*phi(theta) instead was measured against it
   *    and found wrong by up to 46.7 percentile points. `convertThetaToPercentile`
   *    stays as a last-resort fallback for a row that carries no percentile.
   *
   * Together the two moved a real domain by up to 18 points, and on one profile
   * in nine they named a different archetype.
   *
   * A DOMAIN WITH NOTHING MEASURED IS OMITTED rather than defaulted to 50.
   * A 50 asserts "average" about a domain nobody looked at, and it then competes
   * in the top-two sort that picks the constellation.
   *
   * This is the derivation `apc-crm`'s `domainProfile` (assessment-profile.ts)
   * has used since 2026-09-09. The two are now the same arithmetic, which is the
   * point: the coach's copy and the client's results page must not disagree.
   */
  static calculateDomainsFromElements(elements) {
    const domainTotals = {};
    const domainCounts = {};

    // Group elements by domain and calculate averages
    elements.forEach(element => {
      const domainId = this.getElementDomain(element.elementId || element.id);
      if (!domainId) return;

      // Rule 1: only elements the engine actually measured.
      if (!this.isMeasured(element)) return;

      // Rule 2: the engine's own percentile, with theta only as a fallback.
      const score = this.elementPercentile(element);

      if (!domainTotals[domainId]) {
        domainTotals[domainId] = 0;
        domainCounts[domainId] = 0;
      }

      domainTotals[domainId] += score;
      domainCounts[domainId] += 1;
    });

    // Calculate averages. A domain with no measured element is left out.
    const domainScores = {};
    Object.keys(domainTotals).forEach(domainId => {
      if (domainCounts[domainId] > 0) {
        domainScores[domainId] = Math.round(domainTotals[domainId] / domainCounts[domainId]);
      }
    });

    return domainScores;
  }

  /**
   * One stored element score, in the shape the domain arithmetic needs.
   *
   * THE FIELDS IT CARRIES ARE THE POINT. Every call site used to map to
   * `{ elementId, theta }` and drop `reliability` and `strengthPercentile` on
   * the floor, which is why `calculateDomainsFromElements` had nothing better
   * than theta to work with and no way to tell a measured row from an empty
   * one. Adding the rules without adding the fields would have changed nothing.
   */
  static toScoredElement(id, s) {
    return {
      elementId: id,
      theta: s.theta || 0,
      reliability: s.reliability,
      strengthPercentile: s.strengthPercentile
    };
  }

  /**
   * Did the engine measure this element?
   *
   * `reliability > 0` is the signal. A MISSING field reads as measured, because
   * some stored shapes carry no per-element reliability at all and refusing
   * those would throw away a whole profile.
   */
  static isMeasured(element) {
    return element.reliability === undefined || element.reliability === null || element.reliability > 0;
  }

  /**
   * The percentile to score one element on.
   *
   * `strengthPercentile` is the engine's own number and is right by definition.
   * The theta conversion is a fallback for a row that does not carry one.
   */
  static elementPercentile(element) {
    if (typeof element.strengthPercentile === 'number' && Number.isFinite(element.strengthPercentile)) {
      return element.strengthPercentile;
    }
    return this.convertThetaToPercentile(element.theta || 0);
  }
  
  /**
   * Extract domain scores from Premium tier's domainSummary
   * @param {Object} domainSummary - Premium tier domain summary
   * @returns {Object} Domain scores
   */
  static extractDomainsFromSummary(domainSummary) {
    const domainScores = {};
    
    Object.entries(domainSummary).forEach(([domainId, summary]) => {
      // Convert averageTheta to percentile score
      const theta = summary.averageTheta || 0;
      domainScores[domainId] = this.convertThetaToPercentile(theta);
    });
    
    return domainScores;
  }
  
  /**
   * Create standardized topPreferences array for cosmic components
   * @param {Object} rawResults - Raw engine output
   * @param {string} engineType - Detected engine type
   * @returns {Array} Standardized preferences array
   */
  static formatTopPreferences(rawResults, engineType) {
    switch (engineType) {
      case 'FREE':
        // FreeTier provides top 3 measured topPreferences + all 45 Bayesian estimates.
        // Merge to build a full 8-element list for the star grid / constellation:
        //   - Keep the 3 measured elements first (highest fidelity)
        //   - Fill remaining 5 slots from allElementEstimates sorted by strength
        return this.buildFreeTopPreferences(rawResults);
        
      case 'ENHANCED':
        // Convert topElements to topPreferences format
        return this.convertElementsToPreferences(rawResults.topElements || []);
        
      case 'PREMIUM':
        // Take top elements from allElements and convert
        const topElements = (rawResults.allElements || []).slice(0, 8);
        return this.convertElementsToPreferences(topElements);
        
      case 'LEGACY':
        // Convert legacy preferenceScores to array format
        return this.convertLegacyPreferences(rawResults);
        
      default:
        return [];
    }
  }
  
  /**
   * Build a full 8-element topPreferences array for the Free tier.
   * The FreeTierEngine provides:
   *   - topPreferences (3 measured elements from top 2 domains)
   *   - allElementEstimates (all 45 Bayesian estimates, used for Enhanced seeding)
   * We merge these so the star grid and constellation can render 8 stars
   * (3 unlocked + 5 locked). The UI lock logic in CosmicStarGridStandalone
   * and CosmicConstellationTab already handles which stars are unlocked.
   * @param {Object} rawResults - Raw FREE tier engine output
   * @returns {Array} 8-element preferences array
   */
  static buildFreeTopPreferences(rawResults) {
    const measured = rawResults.topPreferences || [];
    const allEstimates = rawResults.allElementEstimates || [];

    // If no Bayesian estimates available, fall back to just enhancing measured ones
    if (allEstimates.length === 0) {
      return this.enhancePreferences(measured);
    }

    // IDs of the 3 measured/revealed elements (already in topPreferences)
    const measuredIds = new Set(measured.map(p => p.id || p.elementId));

    // Sort all estimates by strength (descending) and pick top elements
    // that aren't already in the measured set, to fill up to 8 total
    const additionalElements = [...allEstimates]
      .sort((a, b) => (b.strengthPercentile || 0) - (a.strengthPercentile || 0))
      .filter(el => !measuredIds.has(el.elementId))
      .slice(0, 8 - measured.length)
      .map(el => ({
        id: el.elementId,
        name: el.name,
        domainId: el.domainId,
        score: el.strengthPercentile ? el.strengthPercentile / 100 : 0.5,
        strengthPercentile: el.strengthPercentile || 50,
        rank: el.rank || 0,
        reliability: el.reliability || 0,
        estimateType: el.estimateType || 'bayesian_prior'
      }));

    // Combine: measured first (high fidelity), then additional estimates
    const combined = [...measured, ...additionalElements];

    return this.enhancePreferences(combined);
  }

  /**
   * Enhance preferences with missing metadata
   * @param {Array} preferences - Existing preferences array
   * @returns {Array} Enhanced preferences
   */
  static enhancePreferences(preferences) {
    return preferences.map((pref, index) => ({
      id: pref.id || pref.elementId,
      name: pref.name || this.getElementName(pref.id || pref.elementId),
      domainId: pref.domainId || this.getElementDomain(pref.id || pref.elementId),
      score: typeof pref.score === 'number' ? pref.score : 0.8 - (index * 0.05),
      descriptor: pref.descriptor || this.getElementDescriptor(pref.id || pref.elementId),
      rank: pref.rank || (index + 1), // Preserve global rank if provided by engine
      strengthPercentile: pref.strengthPercentile || Math.round((0.9 - index * 0.05) * 100)
    }));
  }
  
  /**
   * Convert engine elements to preferences format
   * @param {Array} elements - Engine element objects
   * @returns {Array} Preferences array
   */
  static convertElementsToPreferences(elements) {
    return elements.map((element, index) => ({
      id: element.elementId || element.id,
      name: element.name || this.getElementName(element.elementId || element.id),
      domainId: element.domain || this.getElementDomain(element.elementId || element.id),
      score: element.theta ? this.convertThetaToScore(element.theta) : 0.8 - (index * 0.05),
      descriptor: this.getElementDescriptor(element.elementId || element.id),
      rank: element.rank || index + 1,
      strengthPercentile: element.strengthPercentile || this.convertThetaToPercentile(element.theta || 0),
      reliability: element.reliability || 0.8,
      theta: element.theta // Preserve original theta
    }));
  }
  
  /**
   * Convert legacy format to preferences array
   * @param {Object} rawResults - Legacy results object
   * @returns {Array} Preferences array
   */
  static convertLegacyPreferences(rawResults) {
    const { preferenceScores, topPreferences } = rawResults;
    
    if (preferenceScores && typeof preferenceScores === 'object') {
      // Convert object to array
      return Object.values(preferenceScores).map((pref, index) => ({
        ...pref,
        rank: index + 1,
        score: typeof pref.score === 'number' ? pref.score / 100 : 0.8 - (index * 0.05)
      }));
    }
    
    if (Array.isArray(topPreferences)) {
      // Convert simple array to detailed objects
      return topPreferences.map((prefId, index) => ({
        id: prefId,
        name: this.getElementName(prefId),
        domainId: this.getElementDomain(prefId),
        score: 0.9 - (index * 0.05),
        descriptor: this.getElementDescriptor(prefId),
        rank: index + 1,
        strengthPercentile: Math.round((0.9 - index * 0.05) * 100)
      }));
    }
    
    return [];
  }
  
  /**
   * Generate constellation data from top domains
   * @param {Array} topDomains - Array of top domain IDs
   * @returns {Object} Constellation data object
   */
  static generateConstellationData(topDomains) {
    if (topDomains.length < 2) {
      console.warn('Insufficient domains for constellation, using fallback');
      return this.getFallbackConstellation(topDomains[0]);
    }
    
    // Find constellation by top 2 domains
    const constellation = constellations.find(c => 
      c.domains.includes(topDomains[0]) && c.domains.includes(topDomains[1])
    );
    
    if (constellation) {
      return {
        id: constellation.id,
        name: constellation.name,
        description: constellation.description,
        careerDirections: constellation.careerDirections || [],
        domains: constellation.domains,
        emoji: this.getConstellationEmoji(constellation.id),
        tagline: `Your ${constellation.name} constellation combines ${topDomains[0]} and ${topDomains[1]}`
      };
    }
    
    // No exact match found, create custom constellation
    return this.createCustomConstellation(topDomains);
  }
  
  /**
   * Create custom constellation when no official match exists
   * @param {Array} topDomains - Top domain IDs
   * @returns {Object} Custom constellation data
   */
  static createCustomConstellation(topDomains) {
    const domain1 = topDomains[0] || 'insight';
    const domain2 = topDomains[1] || 'vision';
    
    const domainNames = {
      insight: 'Insight',
      vision: 'Vision',
      connection: 'Connection',
      impact: 'Impact',
      structure: 'Structure'
    };
    
    return {
      id: `${domain1}_${domain2}`,
      name: `${domainNames[domain1]} ${domainNames[domain2]} Pattern`,
      description: `Your unique pattern combines ${domainNames[domain1]} and ${domainNames[domain2]}, creating a distinctive approach to work and career development.`,
      careerDirections: [
        'Strategic Analysis',
        'Innovative Problem Solving',
        'Cross-functional Leadership',
        'Systems Development',
        'Creative Strategy'
      ],
      domains: [domain1, domain2],
      emoji: this.getDomainEmoji(domain1),
      tagline: `Your unique ${domainNames[domain1]}-${domainNames[domain2]} constellation`
    };
  }
  
  /**
   * Get fallback constellation for single domain
   * @param {string} domainId - Domain ID
   * @returns {Object} Fallback constellation
   */
  static getFallbackConstellation(domainId) {
    const domainName = this.getDomainName(domainId);
    return {
      id: `${domainId}_focused`,
      name: `${domainName} Specialist`,
      description: `Your pattern shows strong focus in ${domainName}, indicating deep expertise and motivation in this area.`,
      careerDirections: [`${domainName} Specialist`, 'Expert Consultant', 'Technical Leader'],
      domains: [domainId],
      emoji: this.getDomainEmoji(domainId),
      tagline: `Your ${domainName}-focused pattern`
    };
  }
  
  /**
   * Generate metadata and helper fields
   * @param {Object} rawResults - Raw engine output
   * @param {string} engineType - Engine type
   * @returns {Object} Metadata object
   */
  static generateMetadata(rawResults, engineType) {
    return {
      tier: engineType,
      completedAt: rawResults.completedAt || rawResults.timestamp || new Date().toISOString(),
      version: rawResults.version || 'cosmic-processed-v1',
      itemsAdministered: rawResults.itemsAdministered || rawResults.questionsAnswered || 0,
      timeElapsed: rawResults.timeElapsed || 0,
      reliability: rawResults.reliability || rawResults.overallReliability || 0.8
    };
  }
  
  /**
   * Normalize an allElements array so every entry has a resolved elementId and
   * BOTH `domain` and `domainId` (some consumers filter on one, some the other).
   * @param {Array} allElements - Raw element array (entries keyed by domainId)
   * @returns {Array} Normalized element array
   */
  static normalizeAllElements(allElements) {
    if (!Array.isArray(allElements)) return allElements;
    return allElements.map(el => {
      const elementId = el.elementId || el.id || el.element_id;
      const domain = el.domain || el.domainId || el.domain_id || this.getElementDomain(elementId);
      return { ...el, elementId, domain, domainId: domain };
    });
  }

  /**
   * Get top N domains from domain scores
   * @param {Object} domainScores - Domain scores object
   * @param {number} count - Number of top domains to return
   * @returns {Array} Array of domain IDs sorted by score
   */
  static getTopDomains(domainScores, count = 2) {
    return Object.entries(domainScores)
      .sort(([,a], [,b]) => b - a)
      .slice(0, count)
      .map(([domainId]) => domainId);
  }
  
  // ========== UTILITY FUNCTIONS ==========
  
  /**
   * Convert IRT theta to 0-1 score
   * @param {number} theta - IRT theta value
   * @returns {number} Score between 0 and 1
   */
  static convertThetaToScore(theta) {
    // Convert theta (-3 to +3) to 0-1 scale
    return Math.max(0, Math.min(1, (theta + 3) / 6));
  }
  
  /**
   * Convert IRT theta to percentile (0-100) using Normal CDF
   * Uses Abramowitz & Stegun approximation for the standard normal CDF
   * @param {number} theta - IRT theta value (assumed standard normal)
   * @returns {number} Percentile score 0-100
   */
  static convertThetaToPercentile(theta) {
    // Clamp to ±3.5 to avoid extreme values
    const z = Math.max(-3.5, Math.min(3.5, theta));
    const t = 1 / (1 + 0.2316419 * Math.abs(z));
    const d = 0.3989422804014327; // 1/sqrt(2*pi)
    const p = d * Math.exp(-z * z / 2) *
      (t * (0.3193815 + t * (-0.3565638 + t * (1.781478 + t * (-1.8212560 + t * 1.3302744)))));
    const cdf = z > 0 ? 1 - p : p;
    return Math.round(cdf * 100);
  }
  
  /**
   * Get element name by ID
   * @param {string} elementId - Element identifier
   * @returns {string} Human-readable element name
   */
  static getElementName(elementId) {
    if (!elementId || typeof elementId !== 'string') return 'Motivated Element';
    // Search through preferences data for element name
    for (const domainId in preferences) {
      if (typeof preferences[domainId] === 'function') continue;
      
      const element = preferences[domainId].find(p => p.id === elementId);
      if (element) return element.name;
    }
    
    // Fallback: convert camelCase to Title Case
    return elementId.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
  }
  
  /**
   * Get element domain by ID
   * @param {string} elementId - Element identifier
   * @returns {string} Domain ID
   */
  static getElementDomain(elementId) {
    if (!elementId || typeof elementId !== 'string') return null;
    // Search through preferences data for element domain
    for (const domainId in preferences) {
      if (typeof preferences[domainId] === 'function') continue;
      
      const element = preferences[domainId].find(p => p.id === elementId);
      if (element) return domainId;
    }
    
    // Fallback: try to infer from element name
    const name = elementId.toLowerCase();
    if (name.includes('analys') || name.includes('research') || name.includes('data')) return 'insight';
    if (name.includes('creativ') || name.includes('innovat') || name.includes('vision')) return 'vision';
    if (name.includes('team') || name.includes('relationship') || name.includes('commun')) return 'connection';
    if (name.includes('leadership') || name.includes('influence') || name.includes('impact')) return 'impact';
    if (name.includes('organiz') || name.includes('process') || name.includes('structure')) return 'structure';
    
    return 'insight'; // Default fallback
  }
  
  /**
   * Get element descriptor (person descriptor)
   * @param {string} elementId - Element identifier
   * @returns {string} Person descriptor
   */
  static getElementDescriptor(elementId) {
    if (!elementId || typeof elementId !== 'string') return 'Motivated Professional';
    // Search through preferences data for descriptor
    for (const domainId in preferences) {
      if (typeof preferences[domainId] === 'function') continue;
      
      const element = preferences[domainId].find(p => p.id === elementId);
      if (element && element.descriptor) return element.descriptor;
    }
    
    // Generate fallback descriptor based on element
    const descriptors = {
      analysis: 'Strategic Analyst',
      research: 'Knowledge Seeker',
      creativity: 'Creative Visionary',
      leadership: 'Natural Leader',
      teamwork: 'Collaborative Partner',
      innovation: 'Innovation Driver',
      communication: 'Effective Communicator',
      planning: 'Strategic Planner'
    };
    
    // Try to find matching descriptor
    const lowerId = elementId.toLowerCase();
    for (const [key, descriptor] of Object.entries(descriptors)) {
      if (lowerId.includes(key)) return descriptor;
    }
    
    return 'Motivated Professional'; // Ultimate fallback
  }
  
  /**
   * Get domain name by ID
   * @param {string} domainId - Domain identifier
   * @returns {string} Domain name
   */
  static getDomainName(domainId) {
    if (!domainId || typeof domainId !== 'string') return 'Domain';
    const domain = domains.find(d => d.id === domainId);
    return domain ? domain.name : domainId.charAt(0).toUpperCase() + domainId.slice(1);
  }
  
  /**
   * Get domain emoji by ID
   * @param {string} domainId - Domain identifier
   * @returns {string} Domain emoji
   */
  static getDomainEmoji(domainId) {
    const emojis = {
      insight: '🔍',
      vision: '💡',
      connection: '🤝',
      impact: '✨',
      structure: '🔧'
    };
    return emojis[domainId] || '⭐';
  }
  
  /**
   * Get constellation emoji by ID
   * @param {string} constellationId - Constellation identifier
   * @returns {string} Constellation emoji
   */
  static getConstellationEmoji(constellationId) {
    const emojis = {
      luminary: '🗼',
      trailblazer: '🛤️',
      spark: '⚡',
      shaper: '🔨',
      navigator: '🧭',
      scholar: '🎓',
      storykeeper: '📖',
      harborer: '⚓',
      dreamweaver: '🧵',
      anchor: '⚓'
    };
    return emojis[constellationId] || '⭐';
  }
}

export default ResultsProcessor;
